import { Auth } from '@aws-amplify/auth';
import { Amplify, Hub } from '@aws-amplify/core';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import {
  dataResidencyManager,
  SupportedDataResidency,
} from './DataResidencyManager';
import { getStore } from './ApplicationState';
import { push } from './AuthenticationEvents';

interface CognitoConfig {
  region: string; // AWS region for infrastructure
  userPool?: string; // May be called userPool or userPoolId
  userPoolId?: string; // May be called userPool or userPoolId
  identityPool: string;
  clientId: string;
  userPoolArn?: string; // Optional, only present in UAE configs
  hostedUICognitoDomain?: string; // For OAuth/SSO
  hostedUISignInCallbackPath?: string; // For OAuth/SSO
  hostedUISignOutCallbackPath?: string; // For OAuth/SSO
}

// Cache for different data residency configurations
const dataResidencyConfigs: Map<SupportedDataResidency, CognitoConfig> =
  new Map();

// Track current configuration to prevent redundant reconfigurations
let currentlyConfiguredRegion: SupportedDataResidency | null = null;
let hubListenerActive = false;

/**
 * Initialize Amplify configuration for a specific data residency
 */
export async function initializeAmplifyForDataResidency(
  dataResidency: SupportedDataResidency,
): Promise<void> {
  // Skip if already configured for this region
  if (currentlyConfiguredRegion === dataResidency) {
    console.log(
      `✅ Amplify already configured for ${dataResidency}, skipping reconfiguration`,
    );
    return;
  }
  try {
    // Get the appropriate Cognito configuration for the specific data residency
    const cognitoConfigModule =
      await dataResidencyManager.getCognitoConfiguration(dataResidency);
    const cognitoConfig = cognitoConfigModule.default || cognitoConfigModule;

    // Handle both userPool and userPoolId naming conventions
    const userPoolId = cognitoConfig.userPool || cognitoConfig.userPoolId;

    console.log(
      `🔧 Loading config for ${dataResidency}: region=${cognitoConfig.region}, userPool=${userPoolId}`,
    );

    // Build OAuth callbacks if OAuth is configured (currently not in peekapak-common configs but may be added)
    const callbackUrlRoot = `${window.location.protocol}//${window.location.host}`;
    const signInCallback = `${callbackUrlRoot}${
      cognitoConfig.hostedUISignInCallbackPath || '/oauth/login'
    }`;
    const signOutCallback = `${callbackUrlRoot}${
      cognitoConfig.hostedUISignOutCallbackPath || '/'
    }`;

    if (cognitoConfig.hostedUICognitoDomain) {
      console.log(
        `🔧 OAuth callbacks - SignIn: ${signInCallback}, SignOut: ${signOutCallback}`,
      );
    }

    // Configure Amplify for the specific data residency (which maps to AWS regions)
    const amplifyConfig: any = {
      Auth: {
        region: cognitoConfig.region, // AWS region for infrastructure
        userPoolId: userPoolId,
        userPoolWebClientId: cognitoConfig.clientId,
        identityPoolId: cognitoConfig.identityPool,
        mandatorySignIn: true,
        cookieStorage: {
          domain: window.location.hostname,
          path: '/',
          expires: 365,
          secure: window.location.protocol === 'https:',
          sameSite: 'strict',
        },
        authenticationFlowType: 'USER_SRP_AUTH',
      },
    };

    // Add OAuth configuration if available
    if (cognitoConfig.hostedUICognitoDomain) {
      amplifyConfig.oauth = {
        domain: cognitoConfig.hostedUICognitoDomain,
        scope: [
          'phone',
          'email',
          'profile',
          'openid',
          'aws.cognito.signin.user.admin',
        ],
        redirectSignIn: signInCallback,
        redirectSignOut: signOutCallback,
        responseType: 'code', // REFRESH token only generated when responseType is code
      };
    }

    // Configure Amplify
    Amplify.configure(amplifyConfig);

    // Set up Hub listener for auth events only once (needed for OAuth flows)
    if (!hubListenerActive) {
      console.log('🔔 Setting up Hub listener for auth events');
      Hub.listen('auth', (data) => {
        console.log('🔔 Auth event:', data.payload.event);
        try {
          getStore().dispatch(push(data.payload));
        } catch (error) {
          console.error('Failed to dispatch auth event to Redux:', error);
        }
      });
      hubListenerActive = true;
    }

    // Store config in cache
    dataResidencyConfigs.set(dataResidency, {
      ...cognitoConfig,
      userPool: userPoolId, // Normalize to userPool
    });

    // Update the currently configured region
    currentlyConfiguredRegion = dataResidency;

    console.log(
      `✅ Amplify configured for ${dataResidency.toUpperCase()} data residency`,
    );
  } catch (error) {
    console.error(
      `❌ Failed to initialize Amplify for ${dataResidency} data residency:`,
      error,
    );
    throw error;
  }
}

/**
 * Switch user data residency and reinitialize authentication
 */
export async function switchUserDataResidency(
  newDataResidency: SupportedDataResidency,
): Promise<void> {
  try {
    console.log(
      `🔄 Switching user data residency to: ${newDataResidency.toUpperCase()}`,
    );

    // Check if user is currently authenticated
    const currentUser = await getCurrentUser();
    const wasAuthenticated = !!currentUser;

    if (wasAuthenticated) {
      // Sign out from current data residency
      await Auth.signOut({ global: true });
      console.log('📤 Signed out from current data residency');
    }

    // Update data residency manager
    await dataResidencyManager.setDataResidency(newDataResidency);

    // Refresh API endpoints if available
    try {
      const { refreshEndpoints } = await import('./BackendInterface');
      refreshEndpoints();
    } catch (error) {
      console.warn('refreshEndpoints not yet implemented:', error);
    }

    // Reinitialize Amplify for new data residency
    await initializeAmplifyForDataResidency(newDataResidency);

    // If user was authenticated, they'll need to re-authenticate in the new data residency
    if (wasAuthenticated) {
      console.log(
        '⚠️  User was authenticated - will need to re-authenticate in new data residency',
      );
      // Optionally trigger a re-authentication flow
      window.dispatchEvent(
        new CustomEvent('dataResidencySwitched', {
          detail: {
            newDataResidency,
            requiresReauth: true,
          },
        }),
      );
    }
  } catch (error) {
    console.error('❌ Failed to switch user data residency:', error);
    throw error;
  }
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<CognitoUser | null> {
  try {
    const user = await Auth.currentAuthenticatedUser();
    return user;
  } catch (error) {
    // User not authenticated
    return null;
  }
}

/**
 * Detect user data residency and initialize appropriate authentication
 */
export async function initializeDataResidencyAwareAuth(): Promise<void> {
  try {
    // Initialize data residency detection
    await dataResidencyManager.initializeDataResidency();

    const currentDataResidency = dataResidencyManager.getCurrentDataResidency();
    console.log(
      `🌍 Detected user data residency: ${currentDataResidency.toUpperCase()}`,
    );

    // Initialize Amplify for detected data residency
    await initializeAmplifyForDataResidency(currentDataResidency);

    // Set up data residency change listener
    window.addEventListener(
      'userDataResidencyChanged',
      async (event: CustomEvent) => {
        const { newDataResidency } = event.detail;
        if (newDataResidency !== currentDataResidency) {
          await switchUserDataResidency(newDataResidency);
        }
      },
    );
  } catch (error) {
    console.error(
      '❌ Failed to initialize data residency-aware authentication:',
      error,
    );
    // Fallback to US data residency
    await dataResidencyManager.setDataResidency(SupportedDataResidency.US);
    await initializeAmplifyForDataResidency(SupportedDataResidency.US);
  }
}

/**
 * Sign in with data residency-aware configuration
 * This function will attempt fallback to other data residency options
 */
export async function dataResidencyAwareSignIn(
  username: string,
  password: string,
): Promise<any> {
  try {
    // Import the new intelligent sign in function if available
    const { intelligentDataResidencySignIn } = await import(
      './SmartDataResidencyAuth'
    );

    console.log('🔄 Using intelligent multi-data-residency sign in strategy');
    const result = await intelligentDataResidencySignIn(username, password);

    return result.user;
  } catch (error) {
    console.error('❌ Multi-data-residency sign in failed:', error);
    throw error;
  }
}

/**
 * Sign up with data residency-aware configuration
 */
export async function dataResidencyAwareSignUp(
  username: string,
  password: string,
  attributes: any,
): Promise<any> {
  try {
    // Import the new intelligent sign up function if available
    const { intelligentDataResidencySignUp } = await import(
      './SmartDataResidencyAuth'
    );

    console.log('🔄 Using intelligent data residency selection for sign up');
    const result = await intelligentDataResidencySignUp(
      username,
      password,
      attributes,
    );

    return result.user;
  } catch (error) {
    console.error('❌ Intelligent data residency sign up failed:', error);
    throw error;
  }
}

/**
 * Get data residency-specific user pool
 */
export function getDataResidencyAwareUserPool(
  dataResidency?: SupportedDataResidency,
): CognitoUserPool | null {
  const targetDataResidency =
    dataResidency || dataResidencyManager.getCurrentDataResidency();
  const config = dataResidencyConfigs.get(targetDataResidency);

  if (!config) {
    console.warn(
      `⚠️  No configuration found for data residency: ${targetDataResidency}`,
    );
    return null;
  }

  return new CognitoUserPool({
    UserPoolId: config.userPool!,
    ClientId: config.clientId,
  });
}

/**
 * Check if current session is valid for the current data residency
 */
export async function validateDataResidencySession(): Promise<boolean> {
  try {
    const user = await getCurrentUser();
    if (!user) {
      return false;
    }

    const session = await Auth.currentSession();
    const currentDataResidency = dataResidencyManager.getCurrentDataResidency();

    // Check if the token was issued for the current data residency
    const tokenDataResidency =
      session.getIdToken().payload['custom:dataResidency'];

    if (tokenDataResidency && tokenDataResidency !== currentDataResidency) {
      console.warn(
        `⚠️  Token data residency (${tokenDataResidency}) doesn't match current data residency (${currentDataResidency})`,
      );
      return false;
    }

    return session.isValid();
  } catch (error) {
    console.error('❌ Failed to validate data residency session:', error);
    return false;
  }
}

/**
 * Trigger data residency change for existing users
 */
export function triggerDataResidencyChange(
  newDataResidency: SupportedDataResidency,
): void {
  window.dispatchEvent(
    new CustomEvent('userDataResidencyChanged', {
      detail: { newDataResidency },
    }),
  );
}

// Legacy exports for backward compatibility
export { dataResidencyAwareSignIn as regionAwareSignIn };
export { dataResidencyAwareSignUp as regionAwareSignUp };
export { getDataResidencyAwareUserPool as getRegionAwareUserPool };
export { validateDataResidencySession as validateRegionSession };
export { switchUserDataResidency as switchUserRegion };
export { initializeDataResidencyAwareAuth as initializeRegionAwareAuth };
export { triggerDataResidencyChange as triggerRegionChange };
